import React from 'react'
import { Button } from '@/shared/ui'
import LoadingCandles from '@/shared/ui/LoadingCandles'
import { FreemiumPostUpgradeCard } from '@/modules/post-shared'
import type { VisionItem } from '../services/visionService'
import { VisionCard } from './VisionCard'
import { VisionLockedCard } from './VisionLockedCard'
import { useVisionCommunitySummary } from '../hooks/useVisionCommunitySummary'
import { useCommunitySummary } from '@/modules/comunidade/hooks/useCommunitySummary'
import { useLinkedOpportunitiesMap } from '../hooks/useLinkedOpportunitiesMap'
import { useThermometerGate } from '@/modules/termometro/hooks/useThermometerGate'

const LIST_BATCH = 6
const AUTOLOAD_ROOT_MARGIN = '160px 0px 0px 0px'

type VisionListProps = {
  items: VisionItem[]
  communityEnabled: boolean
  currentUserId: string | null
  canDeleteOthers: boolean
  focusVisionId?: number | null
  focusMessageId?: number | null
  showFreemiumCard?: boolean
  hasMore: boolean
  loadingMore: boolean
  onLoadMore?: () => void
  onReload?: () => void
}

export function VisionList({
  items,
  communityEnabled,
  currentUserId,
  canDeleteOthers,
  focusVisionId,
  focusMessageId,
  showFreemiumCard,
  hasMore,
  loadingMore,
  onLoadMore,
  onReload,
}: VisionListProps) {
  const [visibleCount, setVisibleCount] = React.useState(LIST_BATCH)
  const prevTotalRef = React.useRef(0)
  const sentinelRef = React.useRef<HTMLDivElement | null>(null)
  const [autoLoadEnabled, setAutoLoadEnabled] = React.useState(true)

  React.useEffect(() => {
    const total = items.length
    const prevTotal = prevTotalRef.current
    prevTotalRef.current = total
    if (!total) return

    setVisibleCount((current) => {
      if (!current) return Math.min(LIST_BATCH, total)
      if (current > total) return total
      if (prevTotal > 0 && total > prevTotal && current >= prevTotal) {
        return Math.min(current + LIST_BATCH, total)
      }
      return current
    })
  }, [items.length])

  React.useEffect(() => {
    const focusId = focusVisionId != null ? Number(focusVisionId) : null
    if (!focusId || !Number.isFinite(focusId) || focusId <= 0) return

    const idx = items.findIndex((it) => Number(it.id) === focusId)
    if (idx < 0) return

    const required = idx + 1

    setVisibleCount((current) =>
      current >= required ? current : Math.min(required, items.length),
    )
  }, [focusVisionId, items])

  const visibleItems = React.useMemo(() => items.slice(0, visibleCount), [items, visibleCount])
  const visibleAllowedItems = React.useMemo(
    () => visibleItems.filter((it) => it.canView),
    [visibleItems],
  )
  const shouldShowFreemiumCard = showFreemiumCard === true && visibleItems.length > 0
  const hasMoreToShow = visibleCount < items.length
  const showLoadMore = hasMoreToShow || (hasMore && !!onLoadMore)

  const { thermoAllowed, noteForMaster } = useThermometerGate()

  const visibleVisionThreadIds = React.useMemo(
    () =>
      visibleAllowedItems
        .filter((it) => it.linkedOpportunityId === null)
        .map((it) =>
          it.linkedTimelineRootId != null
            ? Number(it.linkedTimelineRootId)
            : Number(it.id),
        )
        .filter((id) => Number.isFinite(id) && id > 0),
    [visibleAllowedItems],
  )

  const timelineRootIdsWithLinks = React.useMemo(() => {
    const ids = new Set<number>()
    visibleAllowedItems.forEach((it) => {
      if (it.linkedTimelineRootId != null) {
        const rootId = Number(it.linkedTimelineRootId)
        if (Number.isFinite(rootId) && rootId > 0) {
          ids.add(rootId)
        }
      }
    })
    return ids
  }, [visibleAllowedItems])

  const visibleLinkedOpportunityIds = React.useMemo(
    () =>
      visibleAllowedItems
        .map((it) => it.linkedOpportunityId)
        .filter((id): id is number => Number.isFinite(Number(id)) && Number(id) > 0)
        .map((id) => Math.floor(Number(id))),
    [visibleAllowedItems],
  )

  const { map: visionCommunitySummaryMap } = useVisionCommunitySummary(
    visibleVisionThreadIds,
    communityEnabled,
  )

  const { map: opportunityCommunitySummaryMap } = useCommunitySummary(
    visibleLinkedOpportunityIds,
    communityEnabled,
  )

  const { map: linkedOpportunitiesMap } = useLinkedOpportunitiesMap(
    visibleLinkedOpportunityIds,
    visibleLinkedOpportunityIds.length > 0,
  )

  const handleLoadMore = React.useCallback(() => {
    if (loadingMore) return

    if (visibleCount < items.length) {
      setVisibleCount((current) => Math.min(current + LIST_BATCH, items.length))
      return
    }

    if (!hasMore) return
    onLoadMore?.()
  }, [hasMore, items.length, loadingMore, onLoadMore, visibleCount])

  React.useEffect(() => {
    if (!showLoadMore) return
    if (!autoLoadEnabled) return

    const el = sentinelRef.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setAutoLoadEnabled(false)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleLoadMore()
          }
        })
      },
      { rootMargin: AUTOLOAD_ROOT_MARGIN },
    )

    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [autoLoadEnabled, handleLoadMore, showLoadMore])

  if (!items.length) {
    return (
      <div className="mt-6 text-center text-sm text-foreground/70">
        Nenhuma visao publicada ainda. Use o botao &quot;Adicionar visao&quot; para criar a primeira.
      </div>
    )
  }

  return (
    <div className="mt-4 space-y-4">
      {visibleItems.map((item, index) => {
        if (!item.canView) {
          return (
            <React.Fragment key={`vision-${item.id}`}>
              <VisionLockedCard
                requiredPlanName={item.requiredPlan?.name || null}
                peopleCount={item.peopleCount}
              />
              {shouldShowFreemiumCard && index === 0 && (
                <FreemiumPostUpgradeCard moduleLabel="Visões" />
              )}
            </React.Fragment>
          )
        }
        const linkedId = item.linkedOpportunityId
        const linkedOpportunity =
          linkedId !== null ? linkedOpportunitiesMap[String(linkedId)] ?? null : null

        const visionThreadId =
          linkedId === null
            ? item.linkedTimelineRootId != null
              ? Number(item.linkedTimelineRootId)
              : Number(item.id)
            : null

        const visionCommunitySummary =
          linkedId === null && visionThreadId != null
            ? visionCommunitySummaryMap?.[String(visionThreadId)] ?? null
            : null

        const opportunityCommunitySummary =
          linkedId !== null ? opportunityCommunitySummaryMap?.[String(linkedId)] ?? null : null

        const effectiveFocusMessageId =
          linkedId === null && focusVisionId && Number(item.id) === Number(focusVisionId)
            ? focusMessageId ?? null
            : null

        const showTimelineOverlay =
          linkedId === null &&
          (item.linkedTimelineRootId != null || timelineRootIdsWithLinks.has(Number(item.id)))

        return (
          <React.Fragment key={`vision-${item.id}`}>
            <VisionCard
              vision={item}
              communityEnabled={communityEnabled}
              currentUserId={currentUserId}
              canDeleteOthers={canDeleteOthers}
              thermoAllowed={thermoAllowed}
              noteForMaster={noteForMaster}
              linkedOpportunity={linkedOpportunity}
              visionCommunitySummary={visionCommunitySummary}
              opportunityCommunitySummary={opportunityCommunitySummary}
              focusMessageId={effectiveFocusMessageId}
              timelineRootId={visionThreadId}
              showTimelineOverlay={showTimelineOverlay}
              onChanged={onReload}
            />
            {shouldShowFreemiumCard && index === 0 && (
              <FreemiumPostUpgradeCard moduleLabel="Visões" />
            )}
          </React.Fragment>
        )
      })}

      {showLoadMore &&
        (autoLoadEnabled ? (
          <div
            ref={sentinelRef}
            className="mt-4 flex w-full items-center justify-center py-2"
          >
            <LoadingCandles className="min-h-[80px]" label="Carregando mais visoes..." />
          </div>
        ) : (
          <div className="flex justify-center pt-4">
            <Button
              type="button"
              variant="outline"
              disabled={loadingMore}
              onClick={handleLoadMore}
            >
              {loadingMore
                ? 'Carregando...'
                : hasMoreToShow
                  ? 'Ver mais'
                  : 'Carregar mais visoes'}
            </Button>
          </div>
        ))}
    </div>
  )
}
